import { apiJobProfiles } from "@/api/job-profile.api.ts";
import { jobRolesKeys } from "@/queries/use-job-roles.query.ts";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { employeePositionsKeys } from "@/queries/employees/use-employee-positions.query.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiJobProfile,
  ApiJobProfileMatrixPayload,
  ApiJobProfileMovePayload,
  ApiJobProfileSavePayload,
  ApiJobProfileSearchPayload,
  getJobProfileTitle,
} from "@/types/job-profile.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SelectItem } from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

export interface JobProfileSelectItem extends SelectItem {
  profile: ApiJobProfile;
}

const getJobProfileSelectItems = (
  jobProfiles: ApiJobProfile[] | undefined
): JobProfileSelectItem[] =>
  [...(jobProfiles ?? [])]
    .sort(
      (left, right) =>
        left.role.title.localeCompare(right.role.title) ||
        getJobProfileTitle(left).localeCompare(getJobProfileTitle(right))
    )
    .map((profile) => {
      const title = getJobProfileTitle(profile);
      const caption = [
        profile.system_title !== title ? profile.system_title : null,
        profile.track?.title,
        profile.grade?.title,
      ]
        .filter(Boolean)
        .join(" · ");

      return {
        id: profile.id,
        title,
        caption,
        profile,
      };
    });

export const jobProfilesKeys = createQueryKeys("jobProfiles", {
  search: (filter: ApiJobProfileSearchPayload) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await apiJobProfiles.search(filter);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  list: (includeArchived: boolean) => ({
    queryKey: [includeArchived],
    queryFn: async () => {
      const result = await apiJobProfiles.getAll(includeArchived);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data ?? [];
    },
  }),
  byId: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await apiJobProfiles.getById(id);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useJobProfilesSearch = (
  filter: ApiJobProfileSearchPayload,
  enabled: boolean = true
) => {
  return useQuery({
    ...jobProfilesKeys.search(filter),
    enabled,
  });
};

export const useJobProfilesSearchInline = (enabled: boolean = true) => {
  const [filter, setFilter] = useState<ApiJobProfileSearchPayload>({
    page_number: 1,
    text: "",
    include_archived: false,
  });
  const { data, isLoading } = useJobProfilesSearch(filter, enabled);
  const options = useMemo(() => getJobProfileSelectItems(data?.items), [data]);

  function search(text: string | undefined) {
    setFilter((currentFilter) => ({ ...currentFilter, page_number: 1, text: text ?? "" }));
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useJobProfiles = (includeArchived: boolean = false, enabled: boolean = true) => {
  const query = useQuery({
    ...jobProfilesKeys.list(includeArchived),
    staleTime: 60 * 1000,
    enabled,
  });
  const options = useMemo(() => getJobProfileSelectItems(query.data), [query.data]);

  return { ...query, options };
};

export const useJobProfileById = (id: number, enabled: boolean = true) => {
  return useQuery({
    ...jobProfilesKeys.byId(id),
    staleTime: 60 * 1000,
    enabled: enabled && id > 0,
  });
};

export interface JobProfileSaveParams {
  id: number;
  payload: ApiJobProfileSavePayload;
}

export const useJobProfileUpdate = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: JobProfileSaveParams) => {
      const result = await apiJobProfiles.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.byId._def });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useJobProfileMatrix = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiJobProfileMatrixPayload) => {
      const result = await apiJobProfiles.matrix(payload);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data ?? [];
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.byId._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.searchInfinite._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useJobProfileMove = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: ApiJobProfileMovePayload }) => {
      const result = await apiJobProfiles.move(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.byId._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.searchInfinite._def });
      void queryClient.invalidateQueries({ queryKey: jobRolesKeys.byId._def });
      void queryClient.invalidateQueries({ queryKey: employeesKeys._def });
      void queryClient.invalidateQueries({ queryKey: employeePositionsKeys._def });
    },
    onError: (error: Error) => onError(error),
  });
};

export const useJobProfileDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiJobProfiles.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.search._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: jobProfilesKeys.byId._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
